import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Modal, Form } from 'react-bootstrap'
import { breakpoints, colors } from 'globalStyles'
import { ReactComponent as HouseIcon } from 'assets/svg/house-building-fa.svg'
import Button from 'react-bootstrap/Button'
import { useTranslation, Trans } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectDeliverySummary } from 'store/shipping/shippingSelectors'
import SuccessModal from 'components/SuccessModal'
import TermsAndConditions from 'components/TermsAndConditions'
import SVGIcon from 'reusableComponents/SVGIcon'
import DropOffLocation from './DropOffLocation'

const StyledPopUp = styled(Modal)`
  .modal-dialog.modal-dialog-centered {
    justify-content: center;
  }

  .modal-header {
    background-color: ${colors.translucent_light_blue};
    padding: 0px;
    min-height: 78px;
    justify-content: center;
  }
  .modal-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 26px;
    margin-left: 13.5px;
  }
  .modal-body {
    text-align: center;
  }

  .modal-dialog {
    @media screen and ${breakpoints.desktop} {
      --bs-modal-width: 500px !important;
    }
    --bs-modal-width: 330px !important;
  }

  .modal-content {
    overflow: hidden; /* Prevent scrolling */
    width: 330px !important;
    height: 726px;
    justify-self: anchor-center;

    @media ${breakpoints.desktop} {
      width: 500px !important;
      height: 683px;
    }
  }

  .modal-footer {
    border-top: none;
    display: flex;
    flex-direction: column;
    margin: -20px 12px 24px;
    padding: 0px;
  }
  .footer-buttons-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 98%;
  }
  .popup-primary-btn,
  .popup-secondary-btn {
    width: 141px !important;
    @media ${breakpoints.desktop} {
      width: 218px !important;
    }
  }

  .footerStyle {
    font-size: 14px;
  }
`

const StyledForm = styled(Form)`
  margin: 0;
  padding: 0;
`

const DropOffLocationPopUp = ({ show, onHide, popUpType, onSave }) => {
  const { t } = useTranslation()
  const [validated, setValidated] = useState(false)
  const {
    buzzerCode,
    additionalDeliveryInstructions,
    remoteSignaturePreference,
    deliveryLocation,
  } = useSelector(selectDeliverySummary)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [loc, setLoc] = useState(deliveryLocation)
  const [successMessage, setSuccessMessage] = useState('')

  const preferencesValueChangeHandler = (event) => {
    setLoc(event.target.value)
  }

  const cancelSuccessModal = () => {
    setShowSuccessModal(false)
  }

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false)
      }, 4000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [showSuccessModal])

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      const form = event.currentTarget

      if (form.checkValidity() === false) {
        event.stopPropagation()
      }
      setValidated(true)
      onSave(additionalDeliveryInstructions, buzzerCode, loc, remoteSignaturePreference)
      onHide()
      const message = t('deliveryPreferencesSummary.drop_off_loc_update_success')
      setSuccessMessage(message)
      setShowSuccessModal(true)
      setTimeout(() => {
        setShowSuccessModal(false)
      }, 3000)
    },
    [additionalDeliveryInstructions, buzzerCode, loc, remoteSignaturePreference, onSave, onHide]
  )

  const modalType = {
    dropOffLocation: {
      title: t('set_preferences.where_to_leave'),
      icon: HouseIcon,
      saveText: t('buttonActions.save'),
      cancelText: t('buttonActions.cancel'),
      component: (
        <DropOffLocation
          id="dropOffLocation"
          onValueChangeHandler={preferencesValueChangeHandler}
          selectedItem={loc}
        />
      ),
      isSaveDisabled: loc === deliveryLocation,
      onCancel: () => {
        setLoc(loc)
        onHide()
      },
      footerContent: (
        <Trans
          t={t}
          i18nKey="deliveryPreferencesSummary.terms_and_conditions_text"
          components={[<TermsAndConditions />]}
        />
      ),
    },
  }

  const modalData = modalType[popUpType]

  // Don't render the form if modal is not shown
  if (!show) {
    return null
  }

  return (
    <StyledForm id="dropOffLocForm" noValidate validated={validated} onSubmit={handleSubmit}>
      <StyledPopUp show={show} backdrop="static" centered>
        <StyledPopUp.Header>
          <SVGIcon
            Icon={modalData.icon}
            color={colors.web_text_black}
            style={{ width: '25px', height: '20px' }}
          />
          <StyledPopUp.Title>{modalData.title}</StyledPopUp.Title>
        </StyledPopUp.Header>
        <StyledPopUp.Body>{modalData.component}</StyledPopUp.Body>
        <StyledPopUp.Footer className="footerStyling">
          <div className="footer-buttons-row">
            <Button
              variant="secondary"
              onClick={modalData.onCancel}
              className="popup-secondary-btn"
            >
              {modalData.cancelText}
            </Button>
            <Button
              type="submit"
              variant="primary"
              form="dropOffLocForm"
              disabled={modalData.isSaveDisabled}
              className="popup-primary-btn"
            >
              {modalData.saveText}
            </Button>
          </div>
          <p className="footerStyle">{modalData.footerContent}</p>
        </StyledPopUp.Footer>
      </StyledPopUp>
      <SuccessModal show={showSuccessModal} onHide={cancelSuccessModal} message={successMessage} />
    </StyledForm>
  )
}

DropOffLocationPopUp.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  popUpType: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default DropOffLocationPopUp
